var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { commonStyle } from '../../utils/commonStyle';
import { margin } from '../../utils/margin';
import { CircularLoader } from '../Loader/CircularLoader';
import { sizeMapping } from './sizeMapping';
import { Icon } from '../Icon/Icon';
import { useTheme } from '../ThemeProvider/useTheme';
import { COLORS } from '../../constants/colors';
import { DEFAULT_SIZE } from './BaseButton';
const cssGlow = css `
  &::before {
    content: '';
    position: absolute;
    width: auto;
    height: 35%;
    left: 4px;
    right: 4px;
    bottom: 7px;
    background-size: 100% 100%;
    background-clip: content-box;
    background-position: center center;
    background-image: ${COLORS.GRADIENTS.RAINBOW};
    display: block;
    filter: blur(16px);
    -webkit-transform: translate3d(0, 0, 0);
  }
`;
const StyledButtonWrapper = styled.button `
  ${commonStyle};
  ${(props) => props.size && sizeMapping[props.size].style}

  font-family: inherit;
  padding: 0;
  margin: 0;
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'inherit')};
  display: ${({ fullWidth }) => (fullWidth ? 'flex' : 'inline-flex')};
  background: none;
  text-decoration: none;
  border: none;

  position: relative;

  &:hover {
    cursor: pointer;
  }

  &:disabled {
    cursor: default;
  }
  ${({ disabled }) => disabled &&
    `
      cursor: default;
      pointer-events: none;

    `}

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0px 0px 0px 3px
        ${({ theme }) => theme.colors.background.default},
      0px 0px 0px 5px ${({ theme }) => theme.colors.content.default};
  }

  ${margin};

  ${(props) => props.glow && !props.disabled && cssGlow}
`;
const StyledButton = styled.span `
  position: relative;
  font-weight: 500;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  display: ${({ fullWidth }) => (fullWidth ? 'flex' : 'inline-flex')};
  justify-content: ${({ justifyContent, fixedIcon }) => justifyContent ? justifyContent : fixedIcon ? 'space-between' : 'center'};

  ${({ disabled, loading, theme }) => !disabled &&
    !loading &&
    `
      color: ${theme.colors.content.above.primary};
      background: ${theme.colors.interactive.primary.default};

      &:hover {
        background: ${theme.colors.interactive.primary.hover};
      }
  `}

  ${({ disabled, theme }) => disabled &&
    `
    background: ${theme.colors.background.contrast};
    color: ${theme.colors.content.disabled};

    &:hover {
      background: ${theme.colors.background.contrast};
    }
  `}

  ${(props) => props.size && sizeMapping[props.size].style}
`;
const BaseGlowButtonComponent = (_a, ref) => {
    var { children, fixedIcon, loading, size = DEFAULT_SIZE, component = 'button', startIcon, endIcon, color, disabled } = _a, props = __rest(_a, ["children", "fixedIcon", "loading", "size", "component", "startIcon", "endIcon", "color", "disabled"]);
    const iconSize = sizeMapping[size].iconSize;
    const theme = useTheme();
    const iconColor = disabled
        ? theme.colors.content.disabled
        : color || theme.colors.content.default;
    if (loading) {
        return (React.createElement(StyledButtonWrapper, Object.assign({ ref: ref, as: component, size: size, theme: theme }, props, { disabled: true }),
            React.createElement(StyledButton, { theme: theme, size: size, fullWidth: props.fullWidth, justifyContent: props.justifyContent, fixedIcon: fixedIcon, disabled: true },
                React.createElement(CircularLoader, { size: iconSize, colorSet: "interactive.secondary.disabled" }))));
    }
    return (React.createElement(StyledButtonWrapper, Object.assign({ ref: ref, as: component, size: size, theme: theme, disabled: disabled, type: "button", glow: props.variant === 'primary' }, props),
        React.createElement(StyledButton, { theme: theme, size: size, disabled: disabled, fullWidth: props.fullWidth, justifyContent: props.justifyContent, fixedIcon: fixedIcon },
            startIcon && (React.createElement(Icon, { name: startIcon, mr: children ? 8 : 0, color: iconColor, size: iconSize })),
            children,
            endIcon && (React.createElement(Icon, { name: endIcon, ml: children ? 8 : 0, color: iconColor, size: iconSize })))));
};
export const BaseGlowButton = forwardRef(BaseGlowButtonComponent);
